type Rgb = [number, number, number];

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const parseColor = (input: string): Rgb | null => {
  const value = input.trim().toLowerCase();

  const hex = value.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/);
  if (hex) {
    const digits =
      hex[1].length === 3
        ? hex[1]
            .split("")
            .map((c) => c + c)
            .join("")
        : hex[1];
    return [0, 2, 4].map((i) => parseInt(digits.slice(i, i + 2), 16)) as Rgb;
  }

  const rgb = value.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/);
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];

  const hsl = value.match(
    /^hsla?\(\s*([\d.-]+)(?:deg)?[\s,]+([\d.]+)%[\s,]+([\d.]+)%/,
  );
  if (hsl) {
    return hslToRgb(Number(hsl[1]), Number(hsl[2]) / 100, Number(hsl[3]) / 100);
  }

  return null;
};

const hslToRgb = (hue: number, saturation: number, lightness: number): Rgb => {
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const sector = (((hue % 360) + 360) % 360) / 60;
  const second = chroma * (1 - Math.abs((sector % 2) - 1));
  const [r, g, b] = [
    [chroma, second, 0],
    [second, chroma, 0],
    [0, chroma, second],
    [0, second, chroma],
    [second, 0, chroma],
    [chroma, 0, second],
  ][Math.min(5, Math.floor(sector))];
  const offset = lightness - chroma / 2;
  return [(r + offset) * 255, (g + offset) * 255, (b + offset) * 255];
};

const channel = (value: number) => {
  const s = value / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};

const luminance = ([r, g, b]: Rgb) =>
  0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);

export const luminanceOf = (color: string): number | null => {
  const rgb = parseColor(color);
  return rgb ? luminance(rgb) : null;
};

export const contrastRatio = (a: Rgb, b: Rgb) => {
  const first = luminance(a);
  const second = luminance(b);
  return (
    (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05)
  );
};

const toHex = ([r, g, b]: Rgb) =>
  "#" +
  [r, g, b]
    .map((v) => Math.round(clamp(v, 0, 255)).toString(16).padStart(2, "0"))
    .join("");

export const ensureContrast = (
  color: string,
  background: string,
  target = 4.5,
): string | null => {
  const foreground = parseColor(color);
  const backdrop = parseColor(background);
  if (!foreground || !backdrop) return null;
  if (contrastRatio(foreground, backdrop) >= target) return null;

  const towardBlack = luminance(backdrop) > 0.18;

  for (let step = 1; step <= 100; step++) {
    const ratio = step / 100;
    const candidate = foreground.map((v) =>
      towardBlack ? v * (1 - ratio) : v + (255 - v) * ratio,
    ) as Rgb;
    if (contrastRatio(candidate, backdrop) >= target) return toHex(candidate);
  }

  return towardBlack ? "#000000" : "#ffffff";
};

export default ensureContrast;
